import { useState } from "react";
import { useI18nStore } from "@/stores/i18nStore";
import { SEO } from "@/components/SEO";
import { JsonLd } from "@/components/JsonLd";
import { ChevronDown, ChevronUp } from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
}

export function FAQPage() {
  const { t } = useI18nStore();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    { question: t.faqQ1, answer: t.faqA1 },
    { question: t.faqQ2, answer: t.faqA2 },
    { question: t.faqQ3, answer: t.faqA3 },
    { question: t.faqQ4, answer: t.faqA4 },
    { question: t.faqQ5, answer: t.faqA5 },
    { question: t.faqQ6, answer: t.faqA6 },
    { question: t.faqQ7, answer: t.faqA7 },
    { question: t.faqQ8, answer: t.faqA8 },
    { question: t.faqQ9, answer: t.faqA9 },
    { question: t.faqQ10, answer: t.faqA10 },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="FAQ - Frequently Asked Questions"
        description="Get answers to common questions about HealthPass: How it works, GDPR compliance, pricing, emergency access, and more. Your health data security questions answered."
        keywords="healthpass faq, health data questions, QR code health faq, travel health questions, medical data security, GDPR health compliance"
        url="https://travelhealthpass.app/faq"
        canonical="https://travelhealthpass.app/faq"
      />
      <JsonLd
        data={{
          "@type": "FAQPage",
          mainEntity: faqs.map((faq) => ({
            "@type": "Question",
            name: faq.question,
            acceptedAnswer: { "@type": "Answer", text: faq.answer },
          })),
        }}
      />

      {/* Header */}
      {/* pt-28, not py-20: the global OfflineBanner is fixed at top-16 and
          covered this heading whenever it was showing. */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white pb-20 pt-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{t.faqTitle}</h1>
          <p className="text-xl text-blue-100">{t.faqSubtitle}</p>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition"
              >
                <span className="text-lg font-semibold text-gray-900">
                  {faq.question}
                </span>
                {openIndex === index ? (
                  <ChevronUp className="h-5 w-5 text-blue-600 flex-shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                )}
              </button>
              {/* Always in the markup, hidden while closed: the prerendered
                  page and its FAQPage data must carry the answers, not only
                  the questions. */}
              <div
                hidden={openIndex !== index}
                className="px-6 py-4 bg-gray-50 border-t border-gray-200"
              >
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {faq.answer}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t.faqStillHaveQuestions}
          </h2>
          <p className="text-gray-600 mb-6">{t.faqContactUs}</p>
          <a
            href="mailto:support@travelhealthpass.app"
            className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition font-semibold"
          >
            {t.contactSupport}
          </a>
        </div>
      </div>
    </div>
  );
}
